/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { track } from "jiffy-analytics";
import { isTouchDevice } from "common/utils/browser";
import { on as onEvent } from "common/utils/ui/event";
import SoftnessClearIcon from "assets/icons/softness-clear.svg?vanilla";
import "./component.scss";

export const urlEnum = {
  print_compositions: "print_compositions"
};

const CLASS_NAMES = {
  JS_SCORE_TOOLTIP: ".js-softness-score-tooltip",
  JS_SCORE_NUMBER: ".js-softness-score-number",
  SOFTNESS_SCORE: ".softness-score",
  SOFTNESS_SCORE_MINI: ".softness-score-mini__number",
  LEARN_MORE: ".softness-score__learn-more"
};

const DPD_DYNAMIC_SOURCES = ["product_details_user_feedback", "product_color_picker"];

const softnessLevels = [
  { label: "Very soft", text: "Ultra-luxe softness" },
  { label: "Soft", text: "Smooth and comfy" },
  { label: "Everyday", text: "Basic comfort" },
  { label: "Rough", text: "Coarse feel" }
];

const tooltipContent = () => {
  const softnessItems = softnessLevels
    .map(
      ({ label, text }) => `
    <div class="flex items-center">
      <div class="w-20 h-6 bg-blue-700 rounded-2xl flex items-center justify-center mr-2">
        <span class="text-white text-sm font-semibold">${label}</span>
      </div>
      <span class="text-gray-600 text-sm">${text}</span>
    </div>
  `
    )
    .join("");

  return `
    <div
      class="w-[274px] bg-white border border-blue-700 rounded-lg shadow-lg"
      id="js-softness-score-tooltip-content"
    >
      <div class="bg-blue-700 rounded-t-lg px-[10.89px] py-[11px] flex items-center justify-between h-10">
        <div class="flex items-center">
          <div class="w-[16.33px] h-[18px] mr-[9px] js-icon-placeholder"></div>
          <h5 class="text-white text-sm font-bold leading-5">
            Softness Score
          </h5>
        </div>
        <a
          class="w-[16.33px] h-[18px] text-white js-softness-score-tooltip-close"
          role="button"
          tabindex="-1"
        ></a>
      </div>
      <div class="p-2">
        <div class="space-y-1">
          ${softnessItems}
        </div>
      </div>
    </div>
  `;
};
const tooltipTemplate = `
  <div class="tooltip softness-tooltip" role="tooltip">
    <div class="tooltip-inner p-0 bg-transparent border-0 rounded-lg max-w-none"></div>
  </div>`;

// Edge detection utilities
const getViewportDimensions = () => ({
  width: window.innerWidth || document.documentElement.clientWidth,
  height: window.innerHeight || document.documentElement.clientHeight
});

const getElementBounds = element => {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height
  };
};

// Smart placement function for Bootstrap tooltip
const getSmartPlacement = (tooltip, element) => {
  const viewport = getViewportDimensions();
  const elementBounds = getElementBounds(element);

  // Estimated tooltip dimensions (approximate)
  const tooltipWidth = 320; // Based on typical content width
  const tooltipHeight = 280; // Based on typical content height
  const arrowOffset = 10; // Arrow size offset

  // Calculate available space in each direction
  const spaceAbove = elementBounds.top;
  const spaceBelow = viewport.height - elementBounds.bottom;
  const spaceLeft = elementBounds.left;
  const spaceRight = viewport.width - elementBounds.right;

  // Determine best vertical placement
  let verticalPlacement = "top";
  if (spaceAbove < tooltipHeight + arrowOffset && spaceBelow > spaceAbove) {
    verticalPlacement = "bottom";
  }

  // Determine best horizontal alignment
  let horizontalPlacement = "";
  const elementCenter = elementBounds.left + elementBounds.width / 2;
  const tooltipHalfWidth = tooltipWidth / 2;

  // Check if tooltip would overflow on the right
  if (elementCenter + tooltipHalfWidth > viewport.width - 20) {
    horizontalPlacement = "-end";
  }
  // Check if tooltip would overflow on the left
  else if (elementCenter - tooltipHalfWidth < 20) {
    horizontalPlacement = "-start";
  }

  // If there's not enough space above or below, try left/right
  if (spaceAbove < tooltipHeight + arrowOffset && spaceBelow < tooltipHeight + arrowOffset) {
    if (spaceRight > tooltipWidth + arrowOffset) {
      return "right";
    }

    if (spaceLeft > tooltipWidth + arrowOffset) {
      return "left";
    }
  }

  return verticalPlacement + horizontalPlacement;
};

// Additional positioning adjustment after tooltip is shown
const adjustTooltipPosition = triggerElement => {
  const tooltipId = triggerElement.getAttribute("aria-describedby");
  if (!tooltipId) return;

  const tooltipElement = document.getElementById(tooltipId);
  if (!tooltipElement) return;

  const viewport = getViewportDimensions();
  const tooltipBounds = getElementBounds(tooltipElement);

  // Check if tooltip is going outside viewport boundaries
  let adjustmentNeeded = false;
  const adjustments = { top: 0, left: 0 };

  // Check right boundary
  if (tooltipBounds.right > viewport.width - 10) {
    adjustments.left = viewport.width - tooltipBounds.right - 10;
    adjustmentNeeded = true;
  }

  // Check left boundary
  if (tooltipBounds.left < 10) {
    adjustments.left = 10 - tooltipBounds.left;
    adjustmentNeeded = true;
  }

  // Check bottom boundary
  if (tooltipBounds.bottom > viewport.height - 10) {
    adjustments.top = viewport.height - tooltipBounds.bottom - 10;
    adjustmentNeeded = true;
  }

  // Check top boundary
  if (tooltipBounds.top < 10) {
    adjustments.top = 10 - tooltipBounds.top;
    adjustmentNeeded = true;
  }

  // Apply adjustments if needed
  if (adjustmentNeeded) {
    const currentTransform = tooltipElement.style.transform || "";
    const currentTranslate = currentTransform.match(/translate(?:3d)?\(([^)]+)\)/);

    let currentX = 0;
    let currentY = 0;
    if (currentTranslate) {
      const values = currentTranslate[1].split(",").map(v => parseFloat(v.trim()));
      currentX = values[0] || 0;
      currentY = values[1] || 0;
    }

    const newX = currentX + adjustments.left;
    const newY = currentY + adjustments.top;

    tooltipElement.style.transform = `translate(${newX}px, ${newY}px)`;
  }
};

const $getTooltips = () => document.querySelectorAll(".js-softness-score-compact-tooltip");
const $$getTooltips = () => $(".js-softness-score-compact-tooltip");

/*  Bootstrap tooltips to stay open on hover https://stackoverflow.com/a/36056707 */
export const initStayOpenOnHover = () => {
  const originalLeave = $.fn.tooltip.Constructor.prototype.leave;

  $.fn.tooltip.Constructor.prototype.leave = function leave(event) {
    const { currentTarget } = event;
    const options = this.getDelegateOptions();
    const dynamicDataValue = $(currentTarget)[this.type](options).data(`bs.${this.type}`);
    const self = event instanceof this.constructor ? event : dynamicDataValue;

    originalLeave.call(this, event);

    if (currentTarget) {
      const $$container = $(`#${$(currentTarget).attr("aria-describedby")}`);
      const hasLink = !!$("a[href]", $$container).length;
      if (!hasLink) return;

      const { timeout } = self;
      $$container.one("mouseenter", () => {
        clearTimeout(timeout);

        $$container.one("mouseleave", () => {
          $.fn.tooltip.Constructor.prototype.leave.call(self, self);
        });
      });
    }
  };
};

const $getSoftnessTooltip = $element => {
  const $tooltip = $element
    .closest(CLASS_NAMES.JS_SCORE_TOOLTIP)
    ?.querySelector(CLASS_NAMES.JS_SCORE_NUMBER);

  if ($tooltip) {
    return $tooltip;
  }

  return (
    $element
      .closest(CLASS_NAMES.JS_SCORE_TOOLTIP)
      ?.querySelector(CLASS_NAMES.SOFTNESS_SCORE_MINI) || null
  );
};

const getPageType = () => {
  const currentUrl = window.location.href.replace(/\/+$/, ""); // Remove trailing slashes

  if (currentUrl.includes(".html")) {
    return "detail_page";
  }
  if (currentUrl === window.location.origin || currentUrl === `${window.location.origin}/`) {
    return "home_page";
  }
  return "listing_page";
};

let isLearnMoreOnEventAttached = false;
let activeSoftnessPayload = {};
const initTrackLearnMoreClick = () => {
  if (isLearnMoreOnEventAttached) return;

  const callback = _ => {
    track("softness_score_learn_more_clicked", activeSoftnessPayload);
  };

  onEvent("click", CLASS_NAMES.LEARN_MORE, callback, true);
  isLearnMoreOnEventAttached = true;
};

const trackSoftnessScoreBadgeOpened = $element => {
  const $softnessTooltip = $getSoftnessTooltip($element);
  const score = $softnessTooltip?.textContent.trim() || "";

  const dataset = $softnessTooltip?.dataset;
  const page = dataset?.source?.includes("page") ? dataset.source : getPageType();
  const source = dataset?.source || "left_nav";
  let compositionName = dataset?.compositionName || "";

  if (page === "detail_page" && DPD_DYNAMIC_SOURCES.includes(source)) {
    const urlParams = new URLSearchParams(window.location.search);
    compositionName = urlParams.get("ac") || compositionName;
  }

  const softnessPayload = {
    score,
    source,
    composition_name: compositionName,
    catalog_number: dataset?.catalogNumber || "",
    page
  };

  track("softness_score_badge_opened", softnessPayload);
  activeSoftnessPayload = softnessPayload;
};

const initTooltip = (elements = null) => {
  // If specific elements are provided, use them; otherwise get all tooltips
  const $$tooltips = elements ? $(elements) : $$getTooltips();

  // Filter out already initialized tooltips to prevent duplicates
  const $$uninitializedTooltips = $$tooltips.filter(function filterUninitialized() {
    return !$(this).data("bs.tooltip");
  });

  if ($$uninitializedTooltips.length === 0) return;

  // Unbind any existing event handlers to prevent duplicate bindings
  $$uninitializedTooltips.off("shown.bs.tooltip");

  $$uninitializedTooltips
    .tooltip({
      animation: false,
      container: "body",
      delay: {
        show: isTouchDevice() ? 0 : 500,
        hide: 100
      },
      html: true,
      sanitize: false, // Disable HTML sanitization to allow SVG elements
      title: tooltipContent(),
      template: tooltipTemplate,
      trigger: isTouchDevice() ? "manual" : "hover",
      placement(tooltip, element) {
        // Use smart placement that considers viewport boundaries
        return getSmartPlacement(tooltip, element);
      },
      boundary: "viewport", // Ensure tooltip stays within viewport
      fallbackPlacement: ["top", "bottom", "right", "left"] // Fallback order
    })
    .on("shown.bs.tooltip", function shownCallback() {
      $$tooltips.not(this).tooltip("hide");

      trackSoftnessScoreBadgeOpened(this);

      // Additional positioning adjustment after showing
      adjustTooltipPosition(this);

      // POST-INJECTION SVG ICON APPROACH
      // Why we need this workaround:
      // 1. Bootstrap tooltip sanitization: Even with sanitize:false, some versions of Bootstrap
      //    still filter out SVG elements or <use> tags for security reasons
      // 2. Template string timing: SVG sprites might not be fully loaded when the tooltip
      //    template is initially parsed
      // 3. Dynamic content limitations: Bootstrap's title/content options have limitations
      //    with dynamic SVG sprite references
      // 4. Browser security: Some browsers block SVG sprite references in dynamically
      //    inserted HTML for XSS protection
      //
      // This post-injection approach works because:
      // - The placeholder div passes all sanitization checks (it's just a div)
      // - SVG is injected after the tooltip is fully rendered and in the DOM
      // - useSvg() is called when window.SVG_PATH is guaranteed to be available
      // - The innerHTML assignment happens on an already-trusted DOM element
      const iconPlaceholder = document.querySelector(".js-icon-placeholder");
      if (iconPlaceholder) {
        iconPlaceholder.innerHTML = SoftnessClearIcon({
          class: "w-[16.33px] h-[18px] text-white"
        });
        iconPlaceholder.classList.remove("js-icon-placeholder");
      }
    });

  // For touch devices, manually handle tooltip show/hide
  if (isTouchDevice()) {
    $$uninitializedTooltips.each(function attachClickHandler() {
      const element = this;
      $(element).on("click", e => {
        e.preventDefault();
        e.stopPropagation();

        const $element = $(element);
        const isShown = $element.attr("aria-describedby");

        // Hide all other tooltips
        $$getTooltips().not(element).tooltip("hide");

        // Toggle current tooltip
        $element.tooltip(isShown ? "hide" : "show");
      });
    });
  }
};

const initTooltipClose = () => {
  const callback = e => {
    e.preventDefault();
    $$getTooltips().tooltip("hide");
  };

  onEvent("click", ".js-softness-score-tooltip-close", callback, true);

  // On touch devices, close tooltip when clicking outside
  if (isTouchDevice()) {
    document.addEventListener(
      "click",
      e => {
        // Check if click is outside tooltip and trigger element
        const isTooltipClick = e.target.closest(".tooltip");
        const isTriggerClick = e.target.closest(".js-softness-score-compact-tooltip");

        if (!isTooltipClick && !isTriggerClick) {
          $$getTooltips().tooltip("hide");
        }
      },
      true
    );
  }
};

const stopBubbling = () => {
  $getTooltips().forEach($badge => {
    $badge.addEventListener("click", e => {
      e.preventDefault();
      e.stopPropagation();
    });
  });
};

// Handle window resize to reposition visible tooltips
let resizeTimeout;
const handleWindowResize = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    // Find any visible tooltips and reposition them
    const visibleTooltips = document.querySelectorAll(".tooltip.show");
    visibleTooltips.forEach(tooltip => {
      const triggerId = tooltip.getAttribute("id");
      if (triggerId) {
        const triggerElement = document.querySelector(`[aria-describedby="${triggerId}"]`);
        if (triggerElement) {
          adjustTooltipPosition(triggerElement);
        }
      }
    });
  }, 150); // Debounce resize events
};

// MutationObserver to watch for dynamically added softness score elements
let observer = null;

const initMutationObserver = () => {
  if (observer) return; // Already initialized

  observer = new MutationObserver(mutations => {
    const newTooltipElements = [];

    mutations.forEach(mutation => {
      // Check added nodes for tooltip elements
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) {
          // Element node
          // Check if the node itself is a tooltip element
          if (node.classList && node.classList.contains("js-softness-score-compact-tooltip")) {
            newTooltipElements.push(node);
          }
          // Check for tooltip elements within the added node
          const tooltips = node.querySelectorAll
            ? node.querySelectorAll(".js-softness-score-compact-tooltip")
            : [];
          tooltips.forEach(tooltip => newTooltipElements.push(tooltip));
        }
      });
    });

    // Initialize tooltips for newly added elements
    if (newTooltipElements.length > 0) {
      const currUrl = window.location.href;
      const isPrintCompositionsPage = currUrl.includes(urlEnum.print_compositions);

      if (!isPrintCompositionsPage) {
        initTooltip(newTooltipElements);
      }

      // Stop event bubbling for new elements
      newTooltipElements.forEach($badge => {
        $badge.addEventListener("click", e => {
          e.preventDefault();
          e.stopPropagation();
        });
      });
    }
  });

  // Start observing the body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};

document.addEventListener("DOMContentLoaded", () => {
  const currUrl = window.location.href;

  const isPrintCompositionsPage = currUrl.includes(urlEnum.print_compositions);

  initStayOpenOnHover();

  if (!isPrintCompositionsPage) {
    initTooltip();
    initTrackLearnMoreClick();
  }

  initTooltipClose();
  stopBubbling();

  // Add window resize handler for responsive tooltip positioning
  window.addEventListener("resize", handleWindowResize);

  // Initialize MutationObserver for dynamically added elements
  initMutationObserver();
});
